/* eslint-disable camelcase */
import React, {useEffect, useState} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {Container, useMediaQuery, useTheme} from '@mui/material';

import SubModuleMenuList from './sub-modules-menu-list/SubModuleMenuList';
import SubModuleSearchPage from './sub-module-search-page/SubModuleSearchPage';
import {
  BASE_MENU_ITEM,
  BASE_RECORD_ID,
  BASE_SPECIMEN_NUMBER,
  BASE_SPECIMEN_NUMBER_ID,
  BASE_SPECIMEN_TYPE,
  BASE_LEGACY_APP,
  BASE_UNIQUE_ID,
  MODE,
  IS_FROM_EPIC,
  IS_FROM_ATHENA,
  BASE_DEMOGRAPHIC_ID,
  BASE_PROCEDURE_ID,
  LOCAL_SEARCH,
  BASE_ACCESSION_NUMBER
} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import LegacyAppFilter from './sub-module-page-section/LegacyAppFilter';
import FormConstants from '@shared/constants/FormConstants';
import AuthUtils from 'src/utils/AuthUtils';
import IsBHDialog from '@components/dialog/isBH-dialog/IsBHDialog';
import {useModuleContext} from '@context/ModuleContext';
import {getIsBHStatus} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import Header from 'src/layouts/dashboard/header';
import {useBoolean} from 'src/hooks/use-boolean';
import {Box} from '@mui/system';
import Main from 'src/layouts/dashboard/main';
import {SplashScreen} from 'src/minimal-components/loading-screen';
import {useSettingsContext} from 'src/minimal-components/settings';
import BannerDetailsProvider from '@context/BannerContext';
import {Refresh} from '@mui/icons-material';
import RefreshLeftMenuProvider from '@context/RefreshLeftMenuContext';

const SubModulesMenuHomePage = (): React.ReactElement | null => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const settings = useSettingsContext();

  const nav = useBoolean();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const params: any = useParams();
  const location = useLocation();
  const {enqueueSnackbar} = useSnackbar();
  const {state} = location;
  const data = state?.rowData;
  const [isFilterClicked, setIsFilterClicked] = useState<boolean>(false);
  const [ParentLegacyValue, setParentLegacyValue] = useState('');
  const [bannerDetails, setBannerDetails]: any = useState({});
  const [openIsBHDialog, setOpenIsBHDialog] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {moduleState, setIsBHData, setIsBHReasonData} = useModuleContext();
  const {isBH}: any = moduleState;
  const [loader, setLoader] = useState<boolean>(false);
  const [navVariables, setNavVariables] = useState<any>(null);
  const [isBHSelectionMade, setIsBHSelectionMade] = useState<boolean>(false);
  const [selectedHelpOption, setSelectedHelpOption] = useState('');

  const localSearch = EncryptUtils.getDecryptedData(
    location?.search,
    LOCAL_SEARCH
  );

  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const legacyApp = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_LEGACY_APP
  );

  const specimen_number = localSearch?.length
    ? JSON.parse(localSearch)?.SpecimenNumber
    : EncryptUtils.getDecryptedData(location?.search, BASE_SPECIMEN_NUMBER);
  const specimen_number_id = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_NUMBER_ID
  );
  const accessionNumber = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_ACCESSION_NUMBER
  );
  const specimen_type = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_TYPE
  );

  const risDemographicID = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_DEMOGRAPHIC_ID
  );

  const procedureID = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_PROCEDURE_ID
  );

  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_UNIQUE_ID
  );

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const isFromEpic = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_EPIC
  );

  const isFromAthena = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_ATHENA
  );

  const mode = EncryptUtils.getDecryptedData(location?.search, MODE);

  const specimenNumber = data?.SpecimenNumber ?? specimen_number;
  const specimenNumberId = data?.ID ?? specimen_number_id;

  const handleBannerDetailsAction = async (data: any) => {
    setBannerDetails(data);
  };

  const handleApplyFilterClick = (data: boolean) => {
    setIsFilterClicked(data);
  };

  const handleHelpOptionClick = async (url: string) => {
    setSelectedHelpOption(url);
  };

  // show parent legacyApp filter dropdown
  const renderParentLegacyAppFilter = () => {
    if (params.module_name === FormConstants.MODULE.CLINICAL) {
      if (
        params.submodule_name === undefined ||
        params?.submodule_name === FormConstants.SUB_MODULE.DEMOGRAPHICS ||
        params.submodule_name === FormConstants.SUB_MODULE.REPORTS
      ) {
        return;
      } else {
        return (
          <LegacyAppFilter
            setParentLegacyValue={setParentLegacyValue}
            defaultLegacyAppValue={ParentLegacyValue}
            record_id={recordId}
          />
        );
      }
    }
  };

  const checkIsBHDialog = async () => {
    try {
      const payload = {
        user_id: recordId,
        legacyApp: legacyApp
      };

      const encryptRequestData = EncryptUtils.encryptedData(payload);

      const res = await getIsBHStatus(applicationName, params.module_name, {
        tk: encryptRequestData
      });
      const permissions = AuthUtils.getPermissions();
      const permissionExisted = permissions?.some(
        (ele: any) =>
          ele.object === params?.module_name &&
          ele.function === FormConstants.IS_BH.VIEW_RESTRICTED_DATA
      );
      if (permissionExisted) {
        setIsBHReasonData('User is Authorized to view Restricted Data');
        localStorage.setItem('check_isbh', 'the view restricted data is true');
      }
      if (!res.hasIsBHData && !permissionExisted) {
        localStorage.setItem('check_isbh', 'the view restricted data is false');
      }
      if (res.hasIsBHData && !permissionExisted && !isBH) {
        setOpenIsBHDialog(true);
        setIsBHSelectionMade(false);
      } else {
        setOpenIsBHDialog(false);
        setIsBHSelectionMade(true);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  async function init() {
    if (
      params?.module_name === FormConstants.MODULE.CLINICAL ||
      params?.module_name === FormConstants.MODULE.PATIENT_ACCOUNTING
    ) {
      setLoader(true);
      setIsBHData(false);
      await checkIsBHDialog();
      setLoader(false);
    } else {
      // For other modules, no need to check permissions or show dialog
      setOpenIsBHDialog(false);
      setIsBHSelectionMade(true);
    }
    return () => {
      setIsBHData(false);
    };
  }

  useEffect(() => {
    init();
    AuthUtils.setAuditIdentifier(recordId);
    AuthUtils.setAuditIdentifierType(
      isFromEpic
        ? 'Epic Integraion' + (params?.submodule_name ?? '')
        : isFromAthena
          ? 'Athena Integraion' + (params?.submodule_name ?? '')
          : params?.submodule_name
    );
    AuthUtils.setAuditLegacyApp(legacyApp);

    procedureID &&
      setNavVariables((prev: any) => ({...prev, procedure_id: procedureID}));

    risDemographicID &&
      setNavVariables((prev: any) => ({
        ...prev,
        demographic_id: risDemographicID
      }));
  }, [params?.module_name]);

  useEffect(() => {
    setSelectedHelpOption('');
  }, [params?.submodule_name]);

  const handleDialogClose = (selection: string | null) => {
    if (selection === null) {
      const message = FormConstants.IS_BH.DIALOG_CLOSE_MSG;
      enqueueSnackbar(message, SnackBarConfig.getInfo());
      return;
    }
    setOpenIsBHDialog(false);
    setIsBHSelectionMade(true); // User made a selection
  };

  return loader ? (
    <SplashScreen />
  ) : openIsBHDialog ? (
    <IsBHDialog
      module={params?.module_name}
      showDialog={true}
      setShowDialog={setOpenIsBHDialog}
      triggerFunction={(selection: string | null) => {
        handleDialogClose(selection);
        if (selection === 'Without_Restricted') {
          setIsBHData(true);
        }
      }}
    />
  ) : isBHSelectionMade ? (
    <RefreshLeftMenuProvider>
      <BannerDetailsProvider>
        <Header
          onOpenNav={nav.onTrue}
          module={params.module_name}
          subModule={params?.submodule_name}
          record_id={recordId}
          legacyApp={legacyApp}
          banner_details={bannerDetails}
          renderParentLegacyAppFilter={renderParentLegacyAppFilter}
          ParentLegacyValue={ParentLegacyValue}
          navData={data}
          onHelpOptionClick={handleHelpOptionClick}
        />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: {xs: 'column', lg: 'row'}
          }}>
          {!isMobile && (
            <SubModuleMenuList
              module={params?.module_name}
              sub_module={params?.submodule_name}
              record_id={recordId}
              navData={data}
              unique_id={uniqueId}
              legacyApp={legacyApp}
              menuItem={menuItem}
              isFromEpic={isFromEpic}
              isFromAthena={isFromAthena}
              specimen_number={specimenNumber}
              specimen_number_id={specimenNumberId}
              accessionNumber={accessionNumber}
              specimen_type={specimen_type}
              mode={mode}
              ParentLegacyValue={ParentLegacyValue}
              handleBannerDetailsAction={handleBannerDetailsAction}
              handleApplyFilterClicked={isFilterClicked}
              isOpenNav={nav.value}
              onCloseNav={nav.onFalse}
            />
          )}

          {selectedHelpOption?.length ? (
            <Box
              sx={{
                width: '100%',
                height: '100vh',
                justifyContent: 'center',
                display: 'flex'
              }}>
              <iframe
                src={selectedHelpOption}
                title={'Help Option Content'}
                style={{
                  width: '99%',
                  height: '92vh',
                  marginTop: '68px',
                  borderRadius: '8px',
                  borderColor: '#f4f6f8',
                  border: '0px'
                }}
              />
            </Box>
          ) : (
            <Main>
              <Container maxWidth={settings.themeStretch ? false : 'lg'}>
                <SubModuleSearchPage
                  module={params.module_name}
                  sub_module={params?.submodule_name}
                  record_id={recordId}
                  specimen_number={specimenNumber}
                  specimen_number_id={specimenNumberId}
                  accessionNumber={accessionNumber}
                  procedure_id={navVariables?.procedure_id}
                  demographic_id={navVariables?.demographic_id}
                  specimen_type={specimen_type}
                  legacyApp={legacyApp}
                  ParentLegacyValue={ParentLegacyValue}
                  unique_id={uniqueId}
                  menuItem={menuItem}
                  applyFilterClicked={handleApplyFilterClick}
                />
              </Container>
            </Main>
          )}
        </Box>
      </BannerDetailsProvider>
    </RefreshLeftMenuProvider>
  ) : null;
};
export default SubModulesMenuHomePage;
